/**
 * Procurement API Layer.
 *
 * React Query hooks for purchase orders, line items, and vendors.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";
import type {
  POLineCreate,
  PurchaseOrder,
  PurchaseOrderCreate,
  Vendor,
  VendorCreate,
} from "@/types/purchase-order";

// ---------------------------------------------------------------------------
// Vendors
// ---------------------------------------------------------------------------

export function useVendors(page = 1, pageSize = 100) {
  return useQuery<PaginatedResponse<Vendor>>({
    queryKey: ["vendors", { page, pageSize }],
    queryFn: async () => {
      const resp = await apiClient.get(
        `/api/v1/vendors?page=${page}&page_size=${pageSize}`,
      );
      return resp.data as PaginatedResponse<Vendor>;
    },
  });
}

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation<Vendor, Error, VendorCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post("/api/v1/vendors", data);
      return resp.data as Vendor;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vendors"] }),
  });
}

// ---------------------------------------------------------------------------
// Purchase Orders
// ---------------------------------------------------------------------------

interface UsePOsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  vendorId?: string;
}

export function usePurchaseOrders(params: UsePOsParams = {}) {
  const { page = 1, pageSize = 25, status, vendorId } = params;
  return useQuery<PaginatedResponse<PurchaseOrder>>({
    queryKey: ["purchase-orders", { page, pageSize, status, vendorId }],
    queryFn: async () => {
      const sp = new URLSearchParams();
      sp.set("page", String(page));
      sp.set("page_size", String(pageSize));
      if (status) sp.set("status", status);
      if (vendorId) sp.set("vendor_id", vendorId);
      const resp = await apiClient.get(`/api/v1/purchase-orders?${sp}`);
      return resp.data as PaginatedResponse<PurchaseOrder>;
    },
  });
}

export function usePurchaseOrder(poId: string | undefined) {
  return useQuery<PurchaseOrder>({
    queryKey: ["purchase-orders", poId],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/purchase-orders/${poId}`);
      return resp.data as PurchaseOrder;
    },
    enabled: !!poId,
  });
}

export function useCreatePO() {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, PurchaseOrderCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post("/api/v1/purchase-orders", data);
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchase-orders"] }),
  });
}

export function useUpdatePO(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, Record<string, unknown>>({
    mutationFn: async (data) => {
      const resp = await apiClient.put(`/api/v1/purchase-orders/${poId}`, data);
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] });
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    },
  });
}

export function useAddPOLine(poId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, POLineCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/lines`,
        data,
      );
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useDeletePOLine(poId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (lineId) => {
      await apiClient.delete(`/api/v1/purchase-orders/${poId}/lines/${lineId}`);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useSubmitPO(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, void>({
    mutationFn: async () => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/submit`,
      );
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchase-orders"] }),
  });
}

export function useReceivePO(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, void>({
    mutationFn: async () => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/receive`,
      );
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useReceiveLines(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, string[]>({
    mutationFn: async (lineIds) => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/receive-lines`,
        {
          line_ids: lineIds,
        },
      );
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useApplyDeliveryDate(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, string>({
    mutationFn: async (date) => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/apply-delivery-date`,
        {
          expected_delivery_date: date,
        },
      );
      return resp.data as PurchaseOrder;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useUpdatePOLine(poId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { lineId: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ lineId, data }) => {
      const resp = await apiClient.put(
        `/api/v1/purchase-orders/${poId}/lines/${lineId}`,
        data,
      );
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useAddLineCharge(poId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { lineId: string; charge_type: string; description: string; amount: number }
  >({
    mutationFn: async ({ lineId, ...data }) => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/lines/${lineId}/charges`,
        data,
      );
      return resp.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useDeleteLineCharge(poId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { lineId: string; chargeId: string }>({
    mutationFn: async ({ lineId, chargeId }) => {
      await apiClient.delete(
        `/api/v1/purchase-orders/${poId}/lines/${lineId}/charges/${chargeId}`,
      );
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["purchase-orders", poId] }),
  });
}

export function useFundPO(poId: string) {
  const qc = useQueryClient();
  return useMutation<PurchaseOrder, Error, string>({
    mutationFn: async (creditAccountId) => {
      const resp = await apiClient.post(
        `/api/v1/purchase-orders/${poId}/fund`,
        {
          credit_account_id: creditAccountId,
        },
      );
      return resp.data as PurchaseOrder;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      qc.invalidateQueries({ queryKey: ["credit-accounts"] });
    },
  });
}
